/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-12-02 10:01:50                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-02-26 14:48:02                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Services
  * Validator
  * WorkorderForm: Create/edit workorder form
    * handleChange: handle field change event
*/

/* Imports */
import { useEffect, useRef, useState } from "react";
import { t } from "i18next";
import { api } from "../../../services/api/api";
import { useKeycloak } from "@react-keycloak/web";
import { UserRole, useWorkorderPermissions, WorkorderField } from "../../../hooks/useWorkorderPermissions";
/***/

/* Components */
import FormDropdown from "../../form-dropdown/FormDropdown";
import CustomerSelect from "../customer/CustomerSelect";
import CustomerInfoCard from "../customer/CustomerInfoCard";
import CustomerNoteCallout, { isBlankNote } from "../customer/CustomerNoteCallout";
import CustomerNoteDialog from "../customer/CustomerNoteDialog";
import CustomerReceivables from "../customer/CustomerReceivables";
import ContactSelect from "../contact/ContactSelect";
import TechnicianSelect from "../technician/TechnicianSelect";
import TechnicianInfoCard from "../technician/TechnicianInfoCard";
import AddressSelect from "../address/AddressSelect";
import ContactInfoCard from "../contact/ContactInfoCard";
import Switch from "../../switch/Switch";
import CompInfosForm, { compInfoValidatorFor } from "../compinfos/CompInfosForm";
import DateRangeSelect from "../date-range/DateRangeSelect";
/***/

/* Services */
import { validator } from "../../../services/form-validator/form-validator";
import MachineSelect from "../machines/MachineSelect";
import { PhoneIcon } from "@heroicons/react/24/solid";
import { Input, Tooltip, Typography } from "@material-tailwind/react";
import { WorkorderStatus } from "../../../types/workorder/workorderStatus";
import WorkordersStatusDisplay from "../workorder-status/WorkordersStatusDisplay";
import WOTypeSelect from "./WOTypeSelect";
import WOOriginSelect from "./WOOriginSelect";
import { WorkorderValidator } from "./workorder-validator";
import { getWorkorderTimeError, getWorkorderTimeIssue } from "./workorder-time-rules";
/***/

/* Validator */
export const WorkorderFormValidator = {
  customer: (e: unknown) => validator.isNotNull(e),
  contacts: (e: unknown) => validator.isNotNull(e) && validator.isArrayNotEmpty(e),
  address: (e: unknown) => validator.isNotNull(e),
  // Le statut vient de l'objet complet : il décide quels compInfos sont saisissables, donc validés
  compInfos: (e: EasyTech.CompInfos, wo: EasyTech.Workorder) => validator.isFormValid(e, compInfoValidatorFor(wo?.status)),
  schedule: (e: unknown) => {
    if (!e) return true;
    const schedule = e as EasyTech.WorkorderSchelude;
    const blank = (value: unknown) => value == null || value === "";
    // Dates planifiées facultatives : les vider doit rester un formulaire valide.
    if (blank(schedule.start) && blank(schedule.end)) return true;
    if (validator.isNumber(schedule.start) && validator.isNumber(schedule.end)) return true;
    if (validator.isString(schedule.start) && validator.isString(schedule.end))
      return new Date(schedule.start).getTime() < new Date(schedule.end).getTime();
    return false;
  },
  time: (e: unknown) => getWorkorderTimeIssue(e) === null,
  purchaseOrderNumber: (e: unknown) => {
    if (e === undefined || e === null || e === "") return true;
    return validator.isString(e);
  }
};

const v = WorkorderFormValidator; // shorthand
/***/

/**
* WorkorderForm: Create/edit workorder form 
* @param data - Data to edit
* @param onChange - On change event, trigger on field change
* @param onSubmit - On submit event, trigger enter key down on field
* @param submitted - is form submitted
*/
export default function WorkorderForm({
  data,
  onChange,
  onSubmit,
  submitted,
  isEditing,
  onToggleShowUnavailable,
  onUnavailablesChange
}: {
  data?: EasyTech.Workorder,
  onChange: (name: string, value: any) => void,
  onSubmit: () => void,
  submitted?: boolean,
  isEditing?: boolean,
  onToggleShowUnavailable?: (show: boolean) => void,
  onUnavailablesChange?: (techs: EasyTech.Technician[]) => void
}) {
  const changeRef = useRef(onChange);
  changeRef.current = onChange;
  const [zoneState, setZoneState] = useState<EasyTech.Zone>();
  const canEditTechStatus = [WorkorderStatus.CREATED, WorkorderStatus.SUSPENDED];

  /*
   * Planner note pop-up, on creation only.
   *
   * A modal is the right tool exactly once: when a planner picks a customer they may never have
   * worked with, before any date is chosen. Everywhere else it was in the way -- it fired again on
   * every edit of an existing intervention, and a dialog that has to be dismissed before work can
   * start gets clicked away unread. The intervention detail carries the note as a post-it instead
   * (CustomerNoteSticky), and the form keeps its inline callout under the customer card, so edit
   * mode still shows the note without interrupting.
   *
   * This form re-renders on every field change, so the dialog is keyed on WHICH customer it was
   * last opened for rather than on a plain boolean: without that, dismissing it and then typing a
   * character in the comments box would pop it straight back up, and the note would be trained out
   * of people within a day. Clearing the customer resets the tracker, so re-picking the same one
   * pops again -- that is a new decision about who the job is for.
   */
  const [noteShownForCustomerId, setNoteShownForCustomerId] = useState<string>();
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);

  const customerId = data?.customer?.id;
  const plannerNote = data?.customer?.plannerNote;

  useEffect(() => {
    if (isEditing) return;

    if (!customerId) {
      setNoteShownForCustomerId(undefined);
      setNoteDialogOpen(false);
      return;
    }

    if (customerId === noteShownForCustomerId) return;
    if (isBlankNote(plannerNote)) {
      // Still mark it seen: a customer with no note must not re-trigger this effect on every render.
      setNoteShownForCustomerId(customerId);
      return;
    }

    setNoteShownForCustomerId(customerId);
    setNoteDialogOpen(true);
  }, [isEditing, customerId, plannerNote, noteShownForCustomerId]);

  // Vérification des rôles utilisateur pour les restrictions d'édition
  const { keycloak } = useKeycloak();
  const { canEditField } = useWorkorderPermissions(keycloak.realmAccess?.roles, data);

  // Désactiver l'édition du customer et contact quand intervention est CLOSED (sauf superadmin)
  const isClosedStatus = isEditing && data?.status === WorkorderStatus.CLOSED;
  const isSuperAdmin = keycloak.realmAccess?.roles.includes(UserRole.SUPER_ADMIN);
  const cantEditCustomerContact = isClosedStatus && !isSuperAdmin;

  // Déterminer qui peut éditer les dates (planifiées et réelles) via le hook centralisé
  const cantEditScheduledTime = isEditing && !canEditField(WorkorderField.SCHEDULED_START_DATE);
  const cantEditRealTime = isEditing && !canEditField(WorkorderField.WO_START_DATE);

  const getStatusLabel = () => {
    if (!data?.status) return "";
    return t(`workorder:status.${data.status.toLowerCase()}`).toLowerCase();
  };

  const getTimeTooltip = (type: "real" | "scheduled") => {
    return t("workorder:cantEditTimeBecauseOfStatus", {
      dateType: t(`workorder:date_type.${type}`),
      status: getStatusLabel()
    });
  };

  // Explains why the real dates are refused, so the red border is not the only clue
  const getRealTimeError = (): string => getWorkorderTimeError(data?.time, t);

  const validateFormData = (formData: any): boolean => {
    if (!formData) return false;
    const validationErrors = WorkorderValidator.validate(formData, t);
    return validationErrors.length === 0;
  };

  const handleSubmitWithValidation = () => {
    if (submitted && !validateFormData(data)) {
      console.warn("Validation échouée - champs obligatoires manquants");
      return;
    }
    onSubmit();
  };

  const cantEditTech = () => {
    return (isEditing && data && !canEditTechStatus.includes(data.status));
  };

  const allowedTechnicianStatusLabels = canEditTechStatus.map((status) => t(`workorder:status.${status.toLowerCase()}`));
  const technicianTooltipContent = `${t("technician:cantEditTechBecauseOfStatus")} ${allowedTechnicianStatusLabels.join(", ")}`.trim();

  // Un range sans début ni fin veut dire « pas de planification », pas un objet dates vides
  // que la validation refuserait et que l'enregistrement réécrirait.
  const onScheduleChange = (value: { start?: string | null, end?: string | null } | null) => {
    if (!value?.start && !value?.end) onChange("schedule", null);
    else onChange("schedule", value);
  };

  const onAddressChange = (value: EasyTech.Address | undefined) => {
    changeRef.current("address", value);
    if (value === undefined || !value.latitude || !value.longitude) return;

    api.get("admin/zones/lat-lng?lat=" + value.latitude + "&lng=" + value.longitude)
      .then((ret) => {
        const zone = ret as EasyTech.Zone;
        if (zone && zone.id)
          setZoneState(zone);
        else
          setZoneState(undefined);
      });
  };

  const technicianEditDisabled = cantEditTech();

  const technicianSelectComponent = (
    <TechnicianSelect onChange={(value) => onChange("technician", value || null)}
      value={data?.technician || undefined}
      clearable={!technicianEditDisabled}
      address={data?.address}
      schedule={data?.schedule}
      disabled={technicianEditDisabled}
      isDetailed={true}
      className="w-full"
      onToggleShowUnavailable={onToggleShowUnavailable}
      onUnavailablesChange={onUnavailablesChange} />
  );

  return (
    <>
      <CustomerNoteDialog
        open={noteDialogOpen}
        note={plannerNote}
        customerName={data?.customer?.name}
        onClose={() => setNoteDialogOpen(false)}
      />

      <FormDropdown label={`${t("common:customer")}  *`}>
        <div className="field full-drawer" data-field="customer">
          {cantEditCustomerContact ? (
            <Tooltip placement="top" content={t("workorder:cantEditCustomerContactBecauseOfStatus")}>
              <div className="w-full">
                <CustomerSelect onChange={(value) => { onChange("customer", value); if (!value || isEditing) onChange("contacts", []); }}
                  value={data && data.customer}
                  isCreatable={false}
                  disabled={true}
                  className={`${(submitted && !validator.isFieldValid(data, "customer", v)) && "error"}`} />
              </div>
            </Tooltip>
          ) : (
            <CustomerSelect onChange={(value) => { onChange("customer", value); if (!value || isEditing) onChange("contacts", []); }}
              value={data && data.customer}
              isCreatable={true}
              isClearable={true}
              className={`${(submitted && !validator.isFieldValid(data, "customer", v)) && "error"}`} />
          )}
        </div>

        {data && data.customer &&
          <CustomerInfoCard customer={data.customer}
            onClear={cantEditCustomerContact ? undefined : () => { onChange("customer", null); onChange("contacts", []); }} />
        }

        {/*
          * Rendered as a sibling of CustomerInfoCard, not inside it: that card is also used by
          * QuotationForm and ProspectForm, which this feature does not cover.
          *
          * data.customer is a CustomerSimpleResponse from CustomerSelect, and core puts plannerNote
          * on the simple response -- so there is nothing to fetch here.
          */}
        {data && data.customer &&
          <CustomerNoteCallout note={data.customer.plannerNote} />
        }

        {/*
          * A sibling of CustomerInfoCard for the same reason the note callout is one: that card is
          * shared with QuotationForm and ProspectForm, and a prospect has no Odoo partner and no
          * invoices. Inline, with no refresh button -- the planner is filling a form here.
          */}
        {data && data.customer &&
          <CustomerReceivables customerId={data.customer.id} variant="inline" />
        }

        {data && data.customer &&
          <div className="field" data-field="contacts">
            <label className="text-gray-900 pb-6 text-base">{t("common:contacts")} *</label>
            {cantEditCustomerContact ? (
              <Tooltip placement="top" content={t("workorder:cantEditCustomerContactBecauseOfStatus")}>
                <div className="w-full">
                  <ContactSelect onChange={(value) => onChange("contacts", [{ contact: value, type: "PRINCIPAL" }])}
                    value={data.contacts && data.contacts[0] && (data.contacts[0].contact || undefined)}
                    customerId={data.customer.id}
                    isCreatable={false}
                    disabled={true}
                    className={`${(submitted && !validator.isFieldValid(data, "contacts", v)) && "error"}`} />
                </div>
              </Tooltip>
            ) : (
              <ContactSelect onChange={(value) => onChange("contacts", value ? [{ contact: value, type: "PRINCIPAL" }] : [])}
                value={data.contacts && data.contacts[0] && (data.contacts[0].contact || undefined)}
                customerId={data.customer.id}
                isCreatable={true}
                isClearable={true}
                className={`${(submitted && !validator.isFieldValid(data, "contacts", v)) && "error"}`} />
            )}
          </div>
        }

        {(data && data.contacts && data.contacts[0] && data.contacts[0].contact) && (
          <>
            <ContactInfoCard contact={data.contacts[0].contact}
              onClear={cantEditCustomerContact ? undefined : () => onChange("contacts", [])} />
          </>
        )}

        {data && data.customer && data.customer.purchaseOrderMandatory && (
          <div className="field" data-field="purchaseOrderNumber">
            <label className="text-gray-900 pb-6 text-base">{t("workorder:infos:purchaseOrderNumber")}</label>
            <input type="text"
              name="purchaseOrderNumber"
              className={`form-field ${(submitted && !validator.isFieldValid(data, "purchaseOrderNumber", v)) && "error"}`}
              placeholder={t("workorder:infos.purchaseOrderNumberPlaceholder")}
              value={data?.purchaseOrderNumber || ""}
              onChange={(e) => onChange(e.target.name, e.target.value)}
              onKeyDown={(e) => e.key === "ENTER" && onSubmit()} />
          </div>
        )}
      </FormDropdown>

      <FormDropdown label={t("workorder:title_single", { ref: "" })}>
        <div className="field">
          <label>{t("common:type")}</label>
          <WOTypeSelect<EasyTech.WorkorderType>
            data={data?.type}
            onChange={(e) => onChange("type", e)}
          />
        </div>

        <div className="field">
          <label>{t("common:origin")}</label>
          <WOOriginSelect<EasyTech.WorkorderOrigin>
            data={data?.origin}
            onChange={(e) => onChange("origin", e)}
          />
        </div>

        {/* TODO: create contact on site component in data/contact */}
        <div className="field">
          <label>{t("common:contactOnSite")}</label>
          <div className="flex flex-row gap-2">
            <input type="text"
              value={data?.onSiteContact?.name || ""}
              onChange={e => onChange("onSiteContact", { ...data?.onSiteContact, name: e.target.value })}
              placeholder={t("workorder:infos:onSiteContact:placeholder")}
              className="form-field" />
            <div className="">
              <Input
                maxLength={16}
                placeholder="+33 7 69 69 69 69"
                value={data?.onSiteContact?.phone || ""}
                pattern="^\+\d{1,3}\s\d{1,4}-\d{1,4}-\d{4}$"
                onChange={e => onChange("onSiteContact", { ...data?.onSiteContact, phone: e.target.value })}
                className="form-field appearance-none placeholder:text-blue-gray-300 placeholder:opacity-100"
                labelProps={{
                  className: "before:content-none after:content-none ",
                }}
                icon={
                  <PhoneIcon className="h-4 w-4 text-blue-gray-600" />
                }
              />
            </div>

          </div>
        </div>
        <AddressSelect value={data && data.address}
          label={`${t("common:address")} *`}
          onChange={(value) => onAddressChange(value)}
          zone={zoneState ?? data?.zone}
          customerAddress={data && data.customer && data.customer.address ? data.customer.address : undefined}
          className={`form-field ${(submitted && !validator.isFieldValid(data, "address", v)) && "error"}`}
          data-field="address" />

        <div className="field">
          <label>{t("common:comments")}</label>
          <textarea value={data ? data.notes || "" : ""}
            onChange={(e) => onChange("notes", e.target.value)}
            placeholder={t("workorder:infos.commentsPlaceholder")}
            maxLength={1000}
            className={`${(data?.notes?.length || 0) > 1000 ? "border-red-500" : ""}`}
          >
          </textarea>

          <div className="flex justify-between items-center mt-1">
            <span className="text-xs text-gray-500">
              {t("workorder:infos.notes.help")}
            </span>
            <span className={`text-xs ${(data?.notes?.length || 0) > 1000 ? "text-red-500 font-semibold" : "text-gray-500"}`}>
              {(data?.notes?.length || 0)}/1000
            </span>
          </div>
        </div>

        <div className="field flex flex-row items-center justify-between full-drawer">
          <div className="flex flex-col">
            <label>{t("common:onCallDuty")}</label>

            <span className="text-sm text-gray-500 mt-1">
              {t("common:onCallDutyDescription")}
            </span>
          </div>

          <div className="ml-auto">
            <Switch checked={data && data.onCallDuty}
              onChange={(e: boolean) => onChange("onCallDuty", e)} />
          </div>
        </div>
      </FormDropdown>

      <FormDropdown label={t("common:schedule")}>
        {cantEditScheduledTime ? (
          <Tooltip placement="top" content={getTimeTooltip("scheduled")}>
            <div className="w-full">
              <DateRangeSelect range={data && data.schedule ? { start: data.schedule.start, end: data.schedule.end, duration: String(data.schedule.duration || "0") } : undefined}
                onChange={onScheduleChange}
                disabled={true}
                className={`p-3 opacity-50 cursor-not-allowed`} />
            </div>
          </Tooltip>
        ) : (
          <DateRangeSelect range={data && data.schedule ? { start: data.schedule.start, end: data.schedule.end, duration: String(data.schedule.duration || "0") } : undefined}
            onChange={onScheduleChange}
            className={`p-3 ${(submitted && !validator.isFieldValid(data, "schedule", v)) && "error"}`} />
        )}
      </FormDropdown>

      {isEditing && data && data.status !== WorkorderStatus.CREATED &&
        <FormDropdown label={t("common:date:real")}>
          {cantEditRealTime ? (
            <>
              {/* Affichage en lecture seule selon les permissions par rôle et statut */}
              <div className="flex flex-col gap-4 p-3">
                <Tooltip placement="top" content={getTimeTooltip("real")}>
                  <div className="field p-0">
                    <label>{t("common:startDate")} *</label>
                    <input
                      type="datetime-local"
                      className="form-field opacity-50 cursor-not-allowed"
                      value={data?.time?.start ? new Date(data.time.start).toISOString().slice(0, 16) : ""}
                      disabled={true}
                    />
                  </div>
                </Tooltip>
                <Tooltip placement="top" content={getTimeTooltip("real")}>
                  <div className="field p-0">
                    <label>{t("common:endDate")} *</label>
                    <input
                      type="datetime-local"
                      className="form-field opacity-50 cursor-not-allowed"
                      value={data?.time?.end ? new Date(data.time.end).toISOString().slice(0, 16) : ""}
                      disabled={true}
                    />
                  </div>
                </Tooltip>
                <Tooltip placement="top" content={getTimeTooltip("real")}>
                  <div className="field p-0">
                    <label>{t("common:durationMin")}</label>
                    <input
                      type="number"
                      className="form-field opacity-50 cursor-not-allowed"
                      value={data?.time?.duration || ""}
                      disabled={true}
                    />
                  </div>
                </Tooltip>
              </div>
              <div className="field">
                <label>{t("form:workorder:travelTime") + " (min)"}</label>
                <Tooltip placement="top" content={getTimeTooltip("real")}>
                  <input type="text"
                    value={data?.travelTime?.duration || ""}
                    disabled={true}
                    placeholder={t("form:workorder:travelTime")}
                    className="form-field opacity-50 cursor-not-allowed" />
                </Tooltip>
              </div>
              <Typography variant="small" color="red" className="mt-2 ml-3">
                {getTimeTooltip("real")}
              </Typography>
            </>
          ) : (
            <>
              <DateRangeSelect range={data && data.time ? { start: data.time.start, end: data.time.end, duration: String(data.time.duration || "0") } : undefined}
                onChange={(value) => onChange("time", { ...data?.time, start: value.start, end: value.end })}
                hideDuration={true}
                disableAutoDuration={true}
                disallowFuture={true}
                className={`p-3 ${(submitted && !validator.isFieldValid(data, "time", v)) && "error"}`} />
              {getRealTimeError() &&
                <Typography variant="small" color="red" className="mt-2 ml-3">
                  {getRealTimeError()}
                </Typography>
              }
              <div className="field">
                <label>{t("common:durationMin")}</label>
                <input type="number"
                  value={data?.time?.duration || ""}
                  onChange={e => onChange("time", { ...data?.time, duration: Number(e.target.value) })}
                  className="form-field" />
              </div>
              <div className="field">
                <label>{t("form:workorder:travelTime") + " (min)"}</label>
                <input type="text"
                  value={data?.travelTime?.duration || ""}
                  onChange={e => onChange("travelTime", { ...data?.travelTime, duration: e.target.value })}
                  placeholder={t("form:workorder:travelTime")}
                  className="form-field" />
              </div>
            </>
          )}
        </FormDropdown>
      }

      {data?.customer && !isEditing &&
        <FormDropdown label={`${t("common:machines")}`}>
          <div className="field">
            <MachineSelect value={data?.machines ? data.machines.map((el) => el.machine) : undefined}
              customerId={data?.customer.id}
              onChange={(e) => onChange("machines", e)} />
          </div>
        </FormDropdown>
      }

      {data?.address &&
        <FormDropdown label={`${t("common:technician")}`}>
          <div className="field full-drawer">
            {technicianEditDisabled ? (
              <Tooltip placement="top" content={technicianTooltipContent}>
                <div className="w-full">
                  {technicianSelectComponent}
                </div>
              </Tooltip>
            ) : (
              technicianSelectComponent
            )}
          </div>

          {data?.technician && !technicianEditDisabled &&
            <TechnicianInfoCard technician={data?.technician} />
          }
          {technicianEditDisabled &&
            <div className="flex gap-2 ml-3 mt-2">
              <Typography variant="small" color="red">
                {t("technician:cantEditTechBecauseOfStatus")}
              </Typography>

              {canEditTechStatus.map((status, i) => (
                <WorkordersStatusDisplay key={`cantEditTechBecauseOfStatus-${i}`} status={status} />
              ))}
            </div>
          }
        </FormDropdown>
      }
      <FormDropdown label={t("common:moreInformations")}>
        <CompInfosForm ressource="WORKORDER"
          data={data?.compInfos}
          onChange={(e) => onChange("compInfos", e)}
          submitted={submitted}
          status={data?.status}
          onSubmit={handleSubmitWithValidation} />
      </FormDropdown>
    </>
  );
}
